/* eslint-disable */
import { mapActions, mapGetters, mapState } from 'vuex';
import marketChangePrice from '@/components/market/market-change-price/market-change-price.vue';
import {
  OPERATIONS,
  VALUES,
  BUTTONS,
  getActiveButtonAfterSwitch,
  thousandDisplay,
} from '@/components/market/market-contract-horizontal-sushi/market-contract-horizontal-sushi.js';
import marketOwnershipAndOrdersBar from '@/components/market/market-ownership-and-orders-bar/market-ownership-and-orders-bar.vue';
import marketSharesBar from '@/components/market/market-shares-bar/market-shares-bar.vue';
import buttonPrice from '@/components/market/v2/button-price/button-price.vue';
import marketDisabledOutcomeV2 from '@/components/market/v2/market-disabled-outcome-v2/market-disabled-outcome-v2.vue';
import marketTradingUnavailableV2 from '@/components/market/v2/market-trading-unavailable-v2/market-trading-unavailable-v2.vue';
import accountVerifyMixin from '@/mixins/accountVerify';
import Contract from '@/models/contract';
import { APP_MOBILE_LAYOUT } from '@/store/modules/app';
import { BOTTOM_WIDGET_FOOTER_MODES } from '@/store/modules/trade';
import {
  isMarketSingle,
  isMarketSettled,
  isMarketDisabled,
  isMarketJourneyDisabled,
  contractOwnershipNum,
} from '@/utils/helpers';

const firstNonEmptyString = (...values) =>
  values.find((value) => typeof value === 'string' && value.trim() !== '');

export default {
  mixins: [accountVerifyMixin],
  components: {
    buttonPrice,
    marketChangePrice,
    marketSharesBar,
    marketDisabledOutcomeV2,
    marketTradingUnavailableV2,
    marketOwnershipAndOrdersBar,
  },
  props: {
    cmsTradeDisabled: { type: Boolean, default: false },
    market: {
      type: Object,
      required: true,
    },
    contract: {
      type: Object,
      required: true,
    },
    forCmsWidget: {
      type: Boolean,
      default: false,
    },
    dashboardMode: {
      type: Boolean,
      default: false,
    },
    showOwnershipAndOrdersBar: {
      type: Boolean,
      default: false,
    },
    mobileLayout: {
      type: Number,
      default: null,
    },
    showSharesOwnedIconOnImage: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      hovered: false,
      BUTTONS,
      OPERATIONS,
      VALUES,
      isWaiting: false,
      activeButton: BUTTONS.BUY_YES.id,
      chart: null,
      sellDisabledTooltipText: "You don't have any shares available to sell.",
    };
  },
  mounted() {
    this.initializePurchaseButtons();
  },
  computed: {
    ...mapGetters('user', ['isLoggedIn']),
    ...mapGetters('app', ['isLayoutMobile', 'isLayoutDesktop', 'currentPriceSymbol']),
    ...mapState('trade', [
      'tradeOptions',
      'tradeOperationToggle',
      'isInConfirmationMode',
      'hidePurchaseForContractId',
    ]),
    ...mapGetters('trade', ['tradeDirectionForMarket']),
    ...mapState('user', ['status']),
    ...mapState('app', ['settingShowOdds', 'settingMobileLayout']),
    hasExplicitMobileLayout() {
      return this.mobileLayout !== null;
    },
    effectiveMobileLayout() {
      if (this.hasExplicitMobileLayout) {
        return this.mobileLayout;
      }

      return this.settingMobileLayout;
    },
    isSimplifiedMobileLayout() {
      return (
        !this.forCmsWidget &&
        this.isLayoutMobile &&
        this.effectiveMobileLayout === APP_MOBILE_LAYOUT.SIMPLIFIED
      );
    },
    isStandardMobileLayout() {
      if (this.forCmsWidget) {
        return true;
      }
      return this.isLayoutMobile && this.effectiveMobileLayout === APP_MOBILE_LAYOUT.STANDARD;
    },
    isCondensedMobileLayout() {
      return (
        this.isLayoutMobile &&
        this.effectiveMobileLayout === APP_MOBILE_LAYOUT.CONDENSED &&
        !this.forCmsWidget
      );
    },
    hideButtons() {
      if (this.forCmsWidget && this.isMarketDisabled) {
        return true;
      }
      if (this.isLayoutMobile && this.isMarketDisabled) {
        return true;
      }

      return (
        this.isSimplifiedMobileLayout &&
        !this.isMarketDisabled &&
        (!this.isContractActive || this.hidePurchaseForContractId === this.contract.contractId)
      );
    },
    isContractActive() {
      return this.tradeOptions && this.tradeOptions.contractId === this.contract.contractId;
    },
    isButtonPriceActive() {
      return !this.forCmsWidget && this.isContractActive;
    },
    showActiveButtonSecondaryPrice() {
      return this.isButtonPriceActive && !this.isCondensedMobileLayout;
    },
    tradeOperationAndValue() {
      return {
        type: this.tradeOptions.operationType,
        value: this.tradeOptions.operationValue,
      };
    },
    contractModel() {
      return new Contract(this.contract);
    },
    isMarketSingle() {
      return isMarketSingle(this.market);
    },
    singleMarketTitle() {
      return firstNonEmptyString(
        this.market && this.market.marketShortName,
        this.contractModel.contractName,
        this.contractModel.name,
      );
    },
    title() {
      return this.isMarketSingle
        ? this.singleMarketTitle
        : this.contractModel.contractName || this.contractModel.name;
    },
    classModifiers() {
      return {
        'market-contract-horizontal-sushi-common-betslip': true,
        'market-contract-horizontal-sushi-common-betslip--show-shares-owned-on-image':
          this.showSharesOwnedIconOnImage,
        'market-contract-horizontal-sushi-common-betslip--disabled': this.isMarketDisabled,
        'market-contract-horizontal-sushi-common-betslip--hovered': this.hovered,
        'market-contract-horizontal-sushi-common-betslip--settled': this.isMarketSettled,
        'market-contract-horizontal-sushi-common-betslip--settled-yes':
          this.isMarketSettled && this.isWinnerYes,
        'market-contract-horizontal-sushi-common-betslip--settled-no':
          this.isMarketSettled && this.isWinnerNo,
        'market-contract-horizontal-sushi-common-betslip--should-close': this.isContractActive,
        'market-contract-horizontal-sushi-common-betslip--with-ownership-and-orders':
          this.showOwnershipAndOrdersSummary,
        'market-contract-horizontal-sushi-common-betslip--dashboard': this.dashboardMode,
        'market-contract-horizontal-sushi-common-betslip--cms-widget': this.forCmsWidget,
        'market-contract-horizontal-sushi-common-betslip--standard':
          this.hasExplicitMobileLayout && this.isStandardMobileLayout,
        'market-contract-horizontal-sushi-common-betslip--simplified':
          this.hasExplicitMobileLayout && this.isSimplifiedMobileLayout,
        'market-contract-horizontal-sushi-common-betslip--condensed': this.isCondensedMobileLayout,
      };
    },
    imageUrl() {
      return this.contractModel.contractImageSmallUrl || this.contractModel.contractImageUrl;
    },
    contractImageStyleModifiers() {
      return {
        'background-image': `url(${this.imageUrl})`,
      };
    },
    sharesOwnedStyleModifiers() {
      return {
        'market-contract-horizontal-sushi-common-betslip__image-shares-owned': true,
        'market-contract-horizontal-sushi-common-betslip__image-shares-owned--no':
          this.contractModel.mode === 'no',
      };
    },
    showOpenOffers() {
      return !this.forCmsWidget && !this.isMarketSettled && this.contractModel.userOpenOrders > 0;
    },
    showSharesBar() {
      return !this.forCmsWidget && !this.isMarketSettled && this.contractModel.userQuantity > 0;
    },
    showSharesBarBottom() {
      return this.showSharesBar && this.isMarketDisabled;
    },
    showOwnershipAndOrdersSummary() {
      return (
        this.showOwnershipAndOrdersBar &&
        !this.isMarketSettled &&
        (this.showSharesBar || this.showOpenOffers)
      );
    },
    isMarketDisabled() {
      return isMarketDisabled(this.market, this.$store);
    },
    isMarketSettled() {
      return isMarketSettled(this.market);
    },
    isMarketJourneyDisabled() {
      return isMarketJourneyDisabled(this.market);
    },
    currentPrice() {
      if (!this.contractModel.hasLastTradePrice) {
        return 0;
      }

      if (this.dashboardMode && this.contractModel.mode === 'no') {
        return 100 - this.contractModel.lastTradePriceAsCents;
      }
      return this.contractModel.lastTradePriceAsCents;
    },
    isWinnerYes() {
      return this.contract.settledStatus === 1;
    },
    isWinnerNo() {
      return this.isMarketSingle && this.contract.settledStatus === 2;
    },
    isContractSaleDisabled() {
      return this.contractModel.userQuantity === 0;
    },
    showChangePriceIndicator() {
      if (!this.contractModel.changePriceAsCents) {
        return false;
      }
      if (this.contractModel.lastClosePrice === null) {
        return false;
      }
      if (this.isMarketSingle) {
        return false;
      }
      return true;
    },
    showNoPriceHintTitle() {
      return (
        this.dashboardMode &&
        this.isLayoutMobile &&
        this.contractModel.mode === VALUES.NO &&
        this.currentPrice > 0
      );
    },
    noPriceHintTitle() {
      return this.settingShowOdds ? 'NO Odds' : 'NO Price';
    },
  },
  watch: {
    // isContractActive(value) {
    //   if (this.isLayoutDesktop) {
    //     if (!value) {
    //       this.activeButton = BUTTONS.BUY_YES.id;
    //     }
    //   }
    // },
    tradeOperationAndValue(v) {
      if (this.contract.contractId !== this.tradeOptions.contractId) {
        return;
      }
      const { type, value } = v;
      if (type && value) {
        if (type === OPERATIONS.SELL) {
          this.activeButton = value === VALUES.YES ? BUTTONS.SELL_YES.id : BUTTONS.SELL_NO.id;
        } else {
          this.activeButton = value === VALUES.YES ? BUTTONS.BUY_YES.id : BUTTONS.BUY_NO.id;
        }
      }
    },
    tradeOperationToggle(switchTo) {
      if (this.forCmsWidget) return;
      if (this.contract.contractId !== this.tradeOptions.contractId || !switchTo) return;
      const button = getActiveButtonAfterSwitch(this.contractModel, switchTo);
      this.toggleTradeOperation(null);
      this.openPurchaseJourney(this.contractModel.contractId, button.type, button.value);
    },
  },
  methods: {
    ...mapActions('trade', [
      'setTradeOptions',
      'toggleTradeOperation',
      'toggleHidePurchaseForContractId',
      'setBottomBetSlipExtendedMode',
      'setBottomWidgetFooterMode',
      'setBottomWidgetFooterModeContractsSection',
    ]),
    ...mapActions('popup', ['setCurrentPopup']),
    ...mapActions('market', ['pushSessionHistoryContract']),
    getDefaultButtonForContract() {
      // A held position or open order on the contract dictates the side the user
      // can act on: 'yes' -> buy/sell YES only, 'no' -> buy/sell NO only.
      if (this.contractModel.mode === VALUES.YES) {
        return BUTTONS.BUY_YES;
      }
      if (this.contractModel.mode === VALUES.NO) {
        return BUTTONS.BUY_NO;
      }
      // No position/orders: fall back to the direction last traded in this market.
      const marketDirection = this.market && this.tradeDirectionForMarket(this.market.marketId);
      return marketDirection === VALUES.NO ? BUTTONS.BUY_NO : BUTTONS.BUY_YES;
    },
    getButtonForTradeOptions() {
      if (
        !this.tradeOptions ||
        !this.tradeOptions.operationType ||
        !this.tradeOptions.operationValue
      ) {
        return this.getDefaultButtonForContract();
      }

      return (
        Object.values(BUTTONS).find(
          (button) =>
            button.type === this.tradeOptions.operationType &&
            button.value === this.tradeOptions.operationValue,
        ) || this.getDefaultButtonForContract()
      );
    },
    findActiveButton() {
      return Object.values(BUTTONS).find((i) => i.id === this.activeButton);
    },
    getTypeAndValueIfButtonActiveOrDefaults() {
      let type = this.tradeOptions.operationType;
      let value = this.tradeOptions.operationValue;
      if (this.activeButton) {
        const button = this.findActiveButton();
        type = button.type;
        value = button.value;
      }
      return { type, value };
    },
    onClickOpenOffers() {
      const { type, value } = this.getTypeAndValueIfButtonActiveOrDefaults();
      this.openPurchaseJourney(this.contractModel.contractId, type, value);
    },
    initializePurchaseButtons() {
      const button = this.getDefaultButtonForContract();
      this.activeButton = button.id;

      if (
        (!this.isMarketSingle || this.isLayoutMobile) &&
        this.tradeOptions &&
        this.contract.contractId === this.tradeOptions.contractId &&
        (button.type !== this.tradeOptions.operationType ||
          button.value !== this.tradeOptions.operationValue)
      ) {
        this.openPurchaseJourney(this.contractModel.contractId, button.type, button.value);
      }
    },
    onMouseOver() {
      this.hovered = true;
    },
    onMouseLeave() {
      this.hovered = false;
    },
    onContractClick() {
      if (this.isCondensedMobileLayout) {
        const { type, value } = this.getTypeAndValueIfButtonActiveOrDefaults();
        this.openPurchaseJourney(this.contractModel.contractId, type, value);
        return;
      }

      if (this.isContractActive && this.isLayoutMobile) {
        if (this.dashboardMode) {
          this.setTradeOptions(null);
          return;
        }
      }

      if (this.tradeOptions.contractId !== this.contract.contractId) {
        const button = this.getDefaultButtonForContract();
        this.openPurchaseJourney(this.contractModel.contractId, button.type, button.value, true);
      } else {
        this.toggleHidePurchaseForContractId(this.contractModel.contractId);
      }
    },
    isButtonActiveClassModifiers(index) {
      if (this.forCmsWidget)
        return { 'market-contract-horizontal-sushi-common-betslip__button-single--off': true };
      const selectedButton = this.isContractActive ? this.getButtonForTradeOptions() : null;
      const isOn = !!selectedButton && selectedButton.id === index;

      return {
        'market-contract-horizontal-sushi-common-betslip__button-single--active':
          this.isContractActive,
        'market-contract-horizontal-sushi-common-betslip__button-single--on': isOn,
        'market-contract-horizontal-sushi-common-betslip__button-single--off': !isOn,
        'market-contract-horizontal-sushi-common-betslip__button-single--off-yes':
          this.tradeOptions.operationValue === VALUES.YES,
        'market-contract-horizontal-sushi-common-betslip__button-single--off-no':
          this.tradeOptions.operationValue === VALUES.NO,
      };
    },
    openPurchaseJourney(
      contractId,
      operationType,
      operationValue,
      dontExtendMobileBetslip = false,
    ) {
      if (this.isWaiting) {
        return;
      }

      // this.isWaiting = true;
      // waitForFirebaseInitialized()
      //   .then(() => {
      //   if (this.isLoggedIn) {
      //     return waitForStatusReady();
      //   }
      //   return Promise.resolve(1);
      // })
      //   .then(() => {
      //
      //   });

      this.isWaiting = false;
      // contract does not modify tradeOptions, is simply used as a widget somewhere
      if (this.forCmsWidget) {
        this.$emit('contractClicked', {
          contractId,
          operationType,
          operationValue,
          priceCents:
            (operationValue === 'no'
              ? this.contractModel.buyNoCostAsCents
              : this.contractModel.buyYesCostAsCents) || null,
        });
        return;
      }
      // if (this.isLoggedIn && !this.isUserVerified) {
      //   this.showAccountVerifyPopup();
      //   return;
      // }
      // if (this.isLoggedIn && !this.status.hasBeenFunded) {
      //   this.showAddFundsPopup({ delay: false });
      //   return;
      // }
      if (this.isMarketJourneyDisabled) return;

      this.pushSessionHistoryContract({
        value: {
          ...this.contract,
          marketName: this.market.marketShortName || this.market.marketName,
        },
      });
      this.setTradeOptions({
        contractId,
        operationType,
        operationValue:
          operationValue !== null ? operationValue : this.contractModel.mode || VALUES.YES,
      });
      if (!dontExtendMobileBetslip) {
        this.setBottomWidgetFooterMode(BOTTOM_WIDGET_FOOTER_MODES.BETSLIP);
        if (this.isLayoutMobile) {
          this.setBottomWidgetFooterModeContractsSection(BOTTOM_WIDGET_FOOTER_MODES.BETSLIP);
        }
        this.setBottomBetSlipExtendedMode(true);
      }
    },
  },
};
